import { Button } from "@/components/core/buttons/Button";
import { cn, modalWidthClass } from "@/lib/utils";
import Image from "next/image";
import { type ComponentProps, useCallback, useEffect } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { Text } from "../Text";
import { IconButton } from "../buttons/IconButton";
import { MixModal } from "./MixModal";

declare global {
    interface Window {
        Kakao: any;
    }
}

export type ShareType = "kakao" | "x";

export interface ShareOptions {
    type?: ShareType;
    id: string;
    title: string;
    abstractcontents: string;
    imgurl: string;
    url: string;
    authorname: string;
}

export const ShareModal = ({
    content,
    onClose,
    ...props
}: {
    content: any;
    onClose?: () => void;
} & Omit<ComponentProps<typeof MixModal>, "content">) => {
    useEffect(() => {
        if (window.Kakao && !window.Kakao.isInitialized()) {
            window.Kakao.init(process.env.NEXT_PUBLIC_KAKAO_JAVASCRIPT_API_KEY);
            console.log("Kakao SDK 초기화 성공");
        } else if (window.Kakao && window.Kakao.isInitialized()) {
            console.log("Kakao SDK 이미 초기화됨");
        } else {
            console.warn("Kakao SDK가 로드되지 않았습니다");
        }
    }, []);
    const [_, copyToClipboard] = useCopyToClipboard();

    const handleShare = useCallback(async (shareOptions: ShareOptions) => {
        const ORIGINAL_CONTENT_URL = `https://mix.day/contents/original/${shareOptions.id}`;
        try {
            switch (shareOptions.type) {
                case "kakao":
                    if (window.Kakao?.Share?.sendCustom) {
                        // window.Kakao.Share.sendCustom({
                        //   templateId: KAKAO_TEMPLATE_ID,
                        //   templateArgs: { title, description: text },
                        // });

                        window.Kakao.Share.sendDefault({
                            objectType: "feed",
                            content: {
                                title: shareOptions.title,
                                // description: shareOptions.abstractcontents,
                                imageUrl: shareOptions.imgurl,
                                link: {
                                    mobileWebUrl: ORIGINAL_CONTENT_URL,
                                    webUrl: ORIGINAL_CONTENT_URL
                                }
                            },
                            itemContent: {
                                profileText: shareOptions.authorname
                                // profileImageUrl:
                                //   "https://mud-kage.kakao.com/dn/Q2iNx/btqgeRgV54P/VLdBs9cvyn8BJXB3o7N8UK/kakaolink40_original.png",
                            },
                            buttons: [
                                {
                                    title: "웹으로 이동",
                                    link: {
                                        mobileWebUrl: ORIGINAL_CONTENT_URL,
                                        webUrl: ORIGINAL_CONTENT_URL
                                    }
                                }
                            ]
                        });
                    } else {
                        console.warn("Kakao SDK가 로드되지 않았습니다.");
                    }
                    break;

                case "x": {
                    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                        `Mix 오리지널 컨텐츠 - ${shareOptions.title}`
                    )}&url=${encodeURIComponent(ORIGINAL_CONTENT_URL)}`;
                    window.open(tweetUrl, "_blank", "noopener");
                    break;
                }

                default:
                    console.warn(
                        `지원하지 않는 공유 타입입니다: ${shareOptions.type}`
                    );
            }
        } catch (err) {
            console.error("공유 중 에러 발생:", err);
        } finally {
            console.log("공유 성공");
        }
    }, []);

    // const shareData = {
    //   title: title,
    //   url: url,
    //   description: description,
    // };

    return (
        <MixModal onRequestClose={onClose} {...props}>
            <div
                className={cn(
                    `rounded-medium bg-white px-6 pb-[7px] pt-6 text-black
                    dark:bg-grey-100 dark:text-white`,
                    modalWidthClass
                )}
            >
                <Text variant="subtitle2" tag="div">
                    공유하기
                </Text>
                <ul className="my-6 flex items-center justify-center gap-6">
                    <li
                        className="flex flex-col items-center justify-center
                            gap-[11px]"
                    >
                        <Button
                            className="h-[60px] w-[60px] bg-transparent p-0
                                hover:bg-transparent hover:text-transparent
                                hover:shadow-none
                                data-[interaction=hover]:bg-transparent
                                data-[interaction=hover]:text-transparent
                                data-[interaction=hover]:shadow-none"
                            onClick={() =>
                                handleShare({
                                    ...content,
                                    type: "kakao"
                                })
                            }
                        >
                            <Image
                                alt="kakao"
                                src="/img/kakao-share.png"
                                quality={100}
                                width={60}
                                height={60}
                            />
                        </Button>
                        <Text variant="body4" tag="div">
                            카카오톡
                        </Text>
                    </li>
                    <li
                        className="flex flex-col items-center justify-center
                            gap-[11px]"
                    >
                        <Button
                            className="h-[60px] w-[60px] bg-transparent p-0
                                hover:bg-transparent hover:text-transparent
                                hover:shadow-none
                                data-[interaction=hover]:bg-transparent
                                data-[interaction=hover]:text-transparent
                                data-[interaction=hover]:shadow-none"
                            onClick={() =>
                                handleShare({
                                    ...content,
                                    type: "x"
                                })
                            }
                        >
                            <Image
                                alt="x"
                                src="/img/x-share.png"
                                quality={100}
                                width={60}
                                height={60}
                            />
                        </Button>
                        <Text variant="body4" tag="div">
                            엑스
                        </Text>
                    </li>
                    <li
                        className="flex flex-col items-center justify-center
                            gap-[11px]"
                    >
                        <IconButton
                            noIconSize
                            name="ShareLink"
                            variant="filled"
                            color="grey"
                            size="xl"
                            className="bg-grey-300 text-black"
                            onClick={() =>
                                copyToClipboard(window.location.href).then(
                                    () => {
                                        alert("링크가 복사되었습니다.");
                                    }
                                )
                            }
                        />
                        <Text variant="body4" tag="div">
                            링크 복사
                        </Text>
                    </li>
                </ul>
            </div>
        </MixModal>
    );
};
